import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AuthActionKind,
  useBusinessCardAuth,
} from "../../../contexts/AuthContext";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomInput } from "../../atoms/CustomInput";
import { CustomButton } from "../../atoms/CustomButton";
import { useBusinessCardRegister } from "../../../hooks/users";
import useIsAuthenticate from "../lmcUserVIew/hooks/useIsAuthenticate";
import { HiArrowLeft, HiCheck, HiExclamationCircle } from "react-icons/hi";
import { getBrowserCountryCode } from "../../../utils/countryDetection";
import { CustomPhoneNumber } from "../../atoms/CustomPhoneNumber";
import { buildBusinessCardAttribution } from "../../../utils/businessCardAttribution";
import { refusalMessageKey } from "../../../utils/refusalMessageKey";
import { BUSINESS_CARD_REGISTER_REFUSALS } from "./businessCardRefusals";

/** Answered with no session on purpose: typing someone's address must never sign the visitor into their account. */
const CONTACT_ALREADY_REGISTERED = "CONTACT_ALREADY_REGISTERED";

interface BusinessCardEmailLoginProps {
  onBackToCard?: () => void;
  entityIdentification: string;
  ownerIdentification: string;
  isDark?: boolean;
}

const BusinessCardEmailLogin: React.FC<BusinessCardEmailLoginProps> = ({
  onBackToCard,
  entityIdentification,
  isDark,
}) => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const { processId } = useParams();
  const { search } = useLocation();
  const isAuthenticated = useIsAuthenticate();

  const {
    state: {
      entity: { entity_data, process_data },
    },
    dispatch,
  } = useBusinessCardAuth();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{
    user_email: string;
    user_first_name?: string;
    user_last_name?: string;
    user_phone?: string;
  }>();

  const { mutate: registerContact, isLoading } = useBusinessCardRegister();

  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const onSubmit = (data: {
    user_email: string;
    user_first_name?: string;
    user_last_name?: string;
    user_phone?: string;
  }) => {
    const cardHolderIdentification =
      process_data?.user_data?.user_identification || "";
    const user_utm_data = buildBusinessCardAttribution();
    registerContact(
      {
        entity_identification: entityIdentification,
        data: {
          user_email: data.user_email,
          user_first_name: data.user_first_name || "",
          user_last_name: data.user_last_name || "",
          user_country_code: getBrowserCountryCode(),
          user_phone: data.user_phone || "",
          owner_identification: cardHolderIdentification,
          ...(user_utm_data ? { user_utm_data } : {}),
        },
      },
      {
        onSuccess(response, variables) {
          setShowErrorMessage(false);
          if (response?.data?.code === CONTACT_ALREADY_REGISTERED) {
            setAlreadyRegistered(true);
            setShowSuccessMessage(true);
            return;
          }
          dispatch({
            type: AuthActionKind.LOGIN,
            payload: {
              ...response.data.element,
              user_email: variables.data.user_email,
            },
          });

          setShowSuccessMessage(true);
        },
        onError(error: any) {
          setErrorMessage(
            t(
              refusalMessageKey(error, BUSINESS_CARD_REGISTER_REFUSALS, "key80")
            ) as string
          );
          setShowErrorMessage(true);
        },
      }
    );
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(
        { pathname: `/business-card/${processId}`, search },
        { replace: true }
      );
    }
  }, [isAuthenticated, navigate, processId, search]);

  return (
    <div className="w-full">
      {onBackToCard && (
        <div className="p-3 sm:p-4 pb-2 border-b border-gray-100">
          <button
            onClick={onBackToCard}
            className={`flex items-center space-x-2 ${
              isDark ? "text-white" : "text-blue-600 hover:text-blue-700"
            } text-sm font-medium`}
          >
            <HiArrowLeft className="w-4 h-4" />
            <span dir="auto" className="text-start">
              {t("key67")}
            </span>
          </button>
        </div>
      )}

      {showSuccessMessage && (
        <div className="p-3 sm:p-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <HiCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div dir="auto" className="text-start">
              <p className="text-green-900 text-sm font-semibold">
                {alreadyRegistered
                  ? t("businessCard.register.alreadyTitle")
                  : t("businessCard.register.successTitle")}
              </p>
              <p className="text-green-800 text-xs mt-0.5">
                {t("businessCard.register.successBody")}
              </p>
            </div>
          </div>
          {onBackToCard && (
            <CustomButton
              variant="primary"
              className="w-full mt-3"
              onClick={onBackToCard}
              title={t("businessCard.register.successCta") as string}
            />
          )}
        </div>
      )}

      {showErrorMessage && (
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center space-x-3">
            <HiExclamationCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p
              dir="auto"
              className="text-amber-800 text-sm font-medium text-start"
            >
              {errorMessage}
            </p>
          </div>
        </div>
      )}

      {!showSuccessMessage && (
        <div className="p-3 sm:p-4 space-y-4">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            dir="auto"
          >
            <div className="space-y-3">
              <CustomInput
                name="user_email"
                type="email"
                control={control}
                error={errors.user_email}
                helperText={
                  errors.user_email?.type === "required"
                    ? (t("key22") as string)
                    : errors.user_email?.type === "pattern"
                    ? (t("key23") as string)
                    : ""
                }
                label={t("key21") as string}
                rules={{
                  required: true,
                  pattern: /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/,
                }}
                labelClassName={isDark ? "text-white" : ""}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CustomInput
                  name="user_first_name"
                  control={control}
                  error={errors.user_first_name}
                  helperText={errors.user_first_name?.message}
                  label={t("key69") as string}
                  rules={{
                    minLength: {
                      value: 2,
                      message: t("key70") as string,
                    },
                    maxLength: {
                      value: 30,
                      message: t("key71") as string,
                    },
                  }}
                  labelClassName={isDark ? "text-white" : ""}
                />

                <CustomInput
                  name="user_last_name"
                  control={control}
                  error={errors.user_last_name}
                  helperText={errors.user_last_name?.message}
                  label={t("key72") as string}
                  rules={{
                    minLength: {
                      value: 2,
                      message: t("key73") as string,
                    },
                    maxLength: {
                      value: 30,
                      message: t("key74") as string,
                    },
                  }}
                  labelClassName={isDark ? "text-white" : ""}
                />
              </div>

              <CustomPhoneNumber
                name="user_phone"
                control={control}
                error={errors.user_phone}
                helperText={errors.user_phone?.message}
                label={t("key75") as string}
                rules={{
                  minLength: {
                    value: 8,
                    message: t("key76") as string,
                  },
                  maxLength: {
                    value: 20,
                    message: t("key77") as string,
                  },
                  pattern: {
                    value: /^[\+]?[0-9\s\-\(\)]+$/,
                    message: t("key78") as string,
                  },
                }}
                labelClassName={isDark ? "text-white" : ""}
                defaultCountryCode={entity_data?.owner_data?.user_country_code}
              />
            </div>

            <CustomButton
              type="submit"
              variant="primary"
              className="w-full"
              disabled={isLoading}
              isLoading={isLoading}
              title={t("businessCard.register.submit") as string}
            />
          </form>
        </div>
      )}
    </div>
  );
};

export default BusinessCardEmailLogin;
